import { formatValue, humanizeMeasure, formatFromColumn } from "@/lib/reports/labels";

import type { ChartBuildCtx, ChartOption } from "./context";
import { unique, sortIfNumeric } from "./helpers";

export function buildHeatmapOption({ viz, query, data, palette, base, measure, time, xKey }: ChartBuildCtx): ChartOption {
  // Honor an explicit y encoding first (a measure-across-two-dimensions heatmap
  // sets encodings.y = the 2nd dimension); fall back to the time member for the
  // common time-on-y heatmap.
  const yDim = viz.encodings.y ?? time ?? "y";
  // Degenerate-heatmap guard: if the AI gave us only a time axis (no
  // second dimension), xKey and yDim both resolve to the time member
  // and we'd render a diagonal stripe. Fall back to a line chart that
  // expresses the same data shape (time × measure).
  const degenerate = xKey === yDim || (query.dimensions ?? []).length === 0;
  if (degenerate) {
    const yFmt = formatFromColumn(measure, data.columns);
    return {
      ...base,
      tooltip: { trigger: "axis", valueFormatter: (v: unknown) => formatValue(v, yFmt) },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: data.rows.map((r) => r[time ?? xKey] as string),
        axisLabel: { color: "#6b7280", fontSize: 11 },
      },
      yAxis: {
        type: "value",
        axisLabel: { color: "#6b7280", fontSize: 11, formatter: (v: number) => formatValue(v, yFmt) },
      },
      series: [
        {
          type: "line",
          name: humanizeMeasure(measure),
          data: data.rows.map((r) => Number(r[measure] ?? 0)),
          smooth: true,
          symbol: "circle",
          symbolSize: 5,
          lineStyle: { width: 2 },
          color: palette[0],
        },
      ],
    } as ChartOption;
  }
  const xValues = sortIfNumeric(unique(data.rows.map((r) => r[xKey] as string)));
  const yValues = sortIfNumeric(unique(data.rows.map((r) => r[yDim] as string)));
  const heatData = data.rows.map((r) => [
    xValues.indexOf(r[xKey] as string),
    yValues.indexOf(r[yDim] as string),
    Number(r[measure] ?? 0),
  ]);
  const maxVal = Math.max(0, ...heatData.map((d) => d[2] as number));
  const measureFmt = formatFromColumn(measure, data.columns);
  return {
    ...base,
    tooltip: {
      position: "top",
      formatter: (p: { value: [number, number, number] }) => {
        const [xi, yi, v] = p.value;
        return `${xValues[xi]} · ${yValues[yi]}<br/><b>${humanizeMeasure(measure)}:</b> ${formatValue(v, measureFmt)}`;
      },
    },
    // Override base grid: reserve room at the bottom for both the
    // rotated x-labels AND the visualMap legend so they don't overlap
    // (eval-heatmap-activity-recruiter-day showed "Floats sent" text
    // landing on top of the day-of-week labels).
    grid: { left: 96, right: 16, top: 16, bottom: 80, containLabel: true },
    xAxis: {
      type: "category",
      data: xValues,
      axisLabel: {
        color: "#6b7280",
        fontSize: 11,
        rotate: 30,
        // Strip leaking cube-path prefix ("day_of_week-A" → "A").
        // Then truncate so headers don't bleed into adjacent cells.
        formatter: (v: string) => {
          if (typeof v !== "string") return v;
          const cleaned = v.replace(/^[a-z][a-z0-9_]*-/i, "");
          return cleaned.length > 14 ? cleaned.slice(0, 13) + "…" : cleaned;
        },
      },
    },
    yAxis: {
      type: "category",
      data: yValues,
      axisLabel: {
        color: "#6b7280",
        fontSize: 11,
        // Y-axis has slightly more horizontal room; allow ~20 chars.
        formatter: (v: string) => {
          if (typeof v !== "string") return v;
          const cleaned = v.replace(/^[a-z][a-z0-9_]*-/i, "");
          return cleaned.length > 20 ? cleaned.slice(0, 19) + "…" : cleaned;
        },
      },
    },
    visualMap: {
      min: 0,
      max: maxVal || 1,
      calculable: true,
      orient: "horizontal",
      left: "center",
      bottom: 4,
      itemWidth: 12,
      itemHeight: 120,
      inRange: { color: ["#eef2ff", palette[0]] },
      text: [humanizeMeasure(measure), ""],
      textStyle: { color: "#6b7280", fontSize: 10 },
      formatter: (v: number) => formatValue(v, measureFmt),
    },
    series: [
      {
        type: "heatmap",
        data: heatData,
        // Show value inside each cell when there's enough room.
        // Threshold: skip the in-cell label when a cell is narrower
        // than ~36px (rough px-per-cell from container width).
        label: {
          show: xValues.length <= 20 && yValues.length <= 20,
          fontSize: 10,
          color: "#1f2937",
          formatter: (p: { value: [number, number, number] }) =>
            formatValue(p.value[2], measureFmt),
        },
        emphasis: { itemStyle: { shadowBlur: 8 } },
      },
    ],
  } as ChartOption;
}
